/**
 * Progressive-web-app wiring: register the service worker and offer an
 * "Add to Home Screen" button.
 *
 * Chrome fires beforeinstallprompt and lets us trigger the real OS dialog.
 * iOS Safari does neither - the only route is Share -> Add to Home Screen -
 * so iOS gets a short instruction card instead of a button that cannot work.
 */
import { track } from './analytics';

const DISMISS_KEY = 'tc-install-dismissed';

function dismissed() {
    try {
        return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
        return false;      // private mode: just show it
    }
}

function remember() {
    try {
        localStorage.setItem(DISMISS_KEY, '1');
    } catch { /* nothing we can do, and nothing that matters */ }
}

const standalone = () =>
    window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

function banner(inner) {
    const el = document.createElement('div');
    el.className = 'pwa-prompt';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-label', 'Add to home screen');
    el.innerHTML =
        '<img src="/icons/icon-192.png" alt="" width="44" height="44">'
        + '<div class="pwa-copy">' + inner + '</div>'
        + '<button type="button" class="pwa-close" aria-label="Not now">&times;</button>';

    el.querySelector('.pwa-close').addEventListener('click', () => {
        el.remove();
        document.body.classList.remove('has-pwa-prompt');
        remember();
        track('pwa_dismissed', {});
    });

    document.body.appendChild(el);
    // A class on <body>, not a sibling selector: the banner is appended last,
    // so it is never a preceding sibling of anything it needs to move.
    document.body.classList.add('has-pwa-prompt');
    requestAnimationFrame(() => el.classList.add('pwa-in'));
    return el;
}

export function initPwa() {
    if ('serviceWorker' in navigator) {
        window.addEventListener('load', () => {
            navigator.serviceWorker.register('/sw.js').catch(() => { /* http, or blocked - the site still works */ });
        });
    }

    if (standalone()) {
        track('pwa_launch', {});    // opened from the home screen icon
        return;
    }

    if (dismissed()) return;

    // ---- Chrome / Edge / Samsung Internet: the real install dialog
    let deferred = null;

    window.addEventListener('beforeinstallprompt', (e) => {
        // Chrome fires this on desktop too. Keep the deferred event either way
        // so the browser's own install button still works, but only interrupt
        // with a banner on a phone or tablet, which is what it is for.
        e.preventDefault();
        deferred = e;

        if (!window.matchMedia('(max-width: 900px), (pointer: coarse)').matches) return;

        const el = banner(
            '<strong>Add Toll Calculator to your home screen</strong>'
            + '<span>Opens like an app, works without a browser bar.</span>'
            + '<button type="button" class="pwa-go">Add</button>'
        );

        el.querySelector('.pwa-go').addEventListener('click', async () => {
            el.remove();
            document.body.classList.remove('has-pwa-prompt');
            deferred.prompt();
            const { outcome } = await deferred.userChoice;
            track('pwa_install_prompt', { outcome });
            if (outcome !== 'accepted') remember();
            deferred = null;
        });

        track('pwa_prompt_shown', { platform: 'android' });
    });

    window.addEventListener('appinstalled', () => track('pwa_installed', {}));

    // ---- iOS Safari: no API, so explain the two taps instead
    const iOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const safari = /safari/i.test(navigator.userAgent) && !/crios|fxios|edgios/i.test(navigator.userAgent);

    if (iOS && safari) {
        // Late enough that it is not the first thing a new visitor sees.
        setTimeout(() => {
            if (dismissed() || standalone()) return;
            banner(
                '<strong>Add to your Home Screen</strong>'
                + '<span>Tap the Share button, then <b>Add to Home Screen</b>.</span>'
            );
            track('pwa_prompt_shown', { platform: 'ios' });
        }, 25000);
    }
}
